import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  Box,
  Button,
  ColorSwatch,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  Icon,
  Menu,
  MenuItem,
  MenuPopup,
  MenuSeparator,
  MenuTrigger,
  ScrollArea,
  Spinner,
  Switch,
  Text,
} from "@salestrak/ui";
import { PageHeader, PageHeaderSubtitle, PageHeaderTitle } from "#app/components/page-header";
import { useIssueSourceSettings } from "#app/lib/hooks/use-issue-source-settings";
import {
  useIssueSources,
  useIssueSourceMutations,
  type IssueSource,
} from "#app/hooks/settings/use-issue-source-mutations";
import { SettingsGroup, SettingsGroupHeader, SettingsGroupRow } from "#app/components/settings/settings-group";
import { IssueSourceFormDialog } from "#app/components/settings/issue-source-form-dialog";
import { TrashConfirmDialog } from "#app/components/settings/trash-confirm-dialog";
import { withPermission } from "#app/components/permissions/require-permission";

function IssueSourcesPage() {
  const { settings, hasData, isLoading, error, updateSetting } = useIssueSourceSettings();
  const { issueSources, isLoading: sourcesLoading } = useIssueSources();
  const {
    createIssueSource,
    updateIssueSource,
    toggleIssueSource,
    deleteIssueSource,
    setDefaultIssueSource,
    clearDefaultIssueSource,
  } = useIssueSourceMutations();

  const [createOpen, setCreateOpen] = useState(false);
  const [editingSource, setEditingSource] = useState<IssueSource | null>(null);
  const [deletingSource, setDeletingSource] = useState<IssueSource | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  if (isLoading && !hasData) {
    return (
      <Box fill="flex" align="center" justify="center">
        <Spinner />
      </Box>
    );
  }

  if (error && !hasData) {
    return (
      <Box fill="flex" align="center" justify="center">
        <Empty>
          <EmptyHeader>
            <EmptyTitle>Unable to load settings</EmptyTitle>
            <EmptyDescription>{error.message}</EmptyDescription>
          </EmptyHeader>
        </Empty>
      </Box>
    );
  }

  return (
    <Box direction="column" fill="flex">
      <ScrollArea>
        <PageHeader centered>
          <PageHeaderTitle>Sources</PageHeaderTitle>
          <PageHeaderSubtitle>
            Defines the lead sources that can be assigned to a project, and whether a source is required.
          </PageHeaderSubtitle>
        </PageHeader>
        <Box align="center" paddingY="md" paddingX="md">
          <Box gap="xl" fill="width" maxWidth={800}>

          <SettingsGroup label="General">
            <SettingsGroupRow
              label="Require source"
              subtitle="A source must be selected in order to create a project."
            >
              <Switch
                checked={settings.requireIssueSource}
                onCheckedChange={(v) => updateSetting("requireIssueSource", v)}
              />
            </SettingsGroupRow>
          </SettingsGroup>

          <SettingsGroup
            label="Source choices"
            subtitle="Define the lead sources available to select."
          >
            <SettingsGroupHeader>
              <Text size="sm" weight="medium">
                {issueSources.length} {issueSources.length === 1 ? "source" : "sources"}
              </Text>
              <Button
                aria-label="Create source"
                variant="ghost"
                size="icon-xs"
                onClick={() => setCreateOpen(true)}
              >
                <Icon name="plus" size="md" />
              </Button>
            </SettingsGroupHeader>

            {sourcesLoading && issueSources.length === 0 ? (
              <Box paddingY="md" align="center" justify="center">
                <Spinner size="lg" />
              </Box>
            ) : null}

            {issueSources.map((source) => (
              <SettingsGroupRow
                key={source.issueSourceId}
                label={`${source.name}${source.isDefault && !source.archived ? " · Default" : ""}`}
                subtitle={source.archived ? "Disabled" : "Enabled"}
                leading={
                  <ColorSwatch
                    color={source.archived ? "grey" : "green"}
                    size="xs"
                    rounded="full"
                  />
                }
                actionsVisible={openMenuId === source.issueSourceId}
              >
                <Menu onOpenChange={(open) => setOpenMenuId(open ? source.issueSourceId : null)}>
                  <MenuTrigger
                    render={
                      <Button
                        aria-label="Source actions"
                        variant="ghost"
                        size="icon-xs"
                      />
                    }
                  >
                    <Icon name="more-horizontal" size="md" />
                  </MenuTrigger>
                  <MenuPopup align="end" className="min-w-45">
                    <MenuItem onClick={() => toggleIssueSource(source)}>
                      <Icon name="eye-off" size="md" />
                      {source.archived ? "Enable source" : "Disable source"}
                    </MenuItem>
                    <MenuItem onClick={() => setEditingSource(source)}>
                      <Icon name="pen-line" size="md" />
                      Edit
                    </MenuItem>
                    {!source.archived && !source.isDefault ? (
                      <MenuItem onClick={() => setDefaultIssueSource(source.id)}>
                        <Icon name="check" size="md" />
                        Set as default
                      </MenuItem>
                    ) : null}
                    {!source.archived && source.isDefault ? (
                      <MenuItem onClick={() => clearDefaultIssueSource()}>
                        <Icon name="x-circle" size="md" />
                        Clear as default
                      </MenuItem>
                    ) : null}
                    <MenuSeparator />
                    <MenuItem
                      variant="destructive"
                      onClick={() => setDeletingSource(source)}
                    >
                      <Icon name="trash-2" size="md" />
                      Delete...
                    </MenuItem>
                  </MenuPopup>
                </Menu>
              </SettingsGroupRow>
            ))}
          </SettingsGroup>
          </Box>
        </Box>
      </ScrollArea>

      <IssueSourceFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSubmit={async (name) => {
          await createIssueSource(name);
        }}
      />

      <IssueSourceFormDialog
        open={!!editingSource}
        onOpenChange={(open) => {
          if (!open) setEditingSource(null);
        }}
        initialName={editingSource?.name}
        onSubmit={async (name) => {
          if (editingSource) {
            await updateIssueSource(editingSource.id, name);
          }
        }}
      />

      <TrashConfirmDialog
        open={!!deletingSource}
        onOpenChange={(open) => {
          if (!open) setDeletingSource(null);
        }}
        title={`Delete "${deletingSource?.name ?? ""}"?`}
        description="This action cannot be undone. Any projects using this source will no longer have a source assigned."
        confirmLabel="Delete"
        onConfirm={async () => {
          if (deletingSource) {
            await deleteIssueSource(deletingSource);
          }
        }}
      />
    </Box>
  );
}

export const Route = createFileRoute("/(app)/$workspace/settings/issue-sources")({
  component: withPermission("issue_sources.manage", IssueSourcesPage),
});
